"use client";

import React, { useState, useEffect, useCallback } from "react";
import { supabase } from "../supabase/client";
import { 
  Save, Ruler, Plus, Trash2, Loader2, 
  CheckCircle2, AlertCircle, Layers, ChevronRight, Edit2, LayoutPanelTop, Eye, EyeOff
} from "lucide-react";

export default function SizeGuideAdmin() {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [categories, setCategories] = useState([]);
  const [sizeGuides, setSizeGuides] = useState([]);
  const [activeTab, setActiveTab] = useState(null);
  const [message, setMessage] = useState({ type: "", text: "" });

 const fetchData = useCallback(async () => {
  setLoading(true);

  const { data: catData } = await supabase
    .from("categories")
    .select("*")
    .order("name");

  const { data: guideData } = await supabase
    .from("size_guides")
    .select("*")
    .order("position", { ascending: true });

  if (catData) {
    setCategories(catData);
    if (catData.length > 0 && !activeTab) {
      setActiveTab(catData[0].id);
    }
  }

  if (guideData) setSizeGuides(guideData);

  setLoading(false);
}, [activeTab]);

 useEffect(() => {
  fetchData();
}, [fetchData]); // ✅ no warning now

  // --- NEW: Toggle Header Visibility ---
  const toggleHeaderVisibility = async (id, currentState) => {
    const newState = !currentState;
    
    // Optimistic Update for UI speed
    setCategories(categories.map(cat => cat.id === id ? { ...cat, on_header: newState } : cat));

    const { error } = await supabase
      .from("categories")
      .update({ on_header: newState })
      .eq("id", id);

    if (error) {
      setMessage({ type: "error", text: "Failed to update header status." });
      // Rollback on error
      setCategories(categories.map(cat => cat.id === id ? { ...cat, on_header: currentState } : cat));
    } else {
      setMessage({ type: "success", text: `Category ${newState ? 'added to' : 'removed from'} header.` });
    }
  };

  // --- Category Handlers ---
  const addCategory = async () => {
    const name = prompt("Enter New Category Name (e.g., Nightwear):");
    if (!name) return;
    const { data, error } = await supabase.from("categories").insert([{ name, on_header: false }]).select();
    if (!error) {
      setCategories([...categories, data[0]]);
      setActiveTab(data[0].id);
    }
  };

  const editCategory = async (id, currentName) => {
    const newName = prompt("Edit Category Name:", currentName);
    if (!newName || newName === currentName) return;

    const { data, error } = await supabase
      .from("categories")
      .update({ name: newName })
      .eq("id", id)
      .select();

    if (!error) {
      setCategories(categories.map(cat => cat.id === id ? data[0] : cat));
    }
  };

  const deleteCategory = async (id, name) => {
    const confirmDelete = window.confirm(`Are you sure you want to delete "${name}"?`);
    if (!confirmDelete) return;

    const { error } = await supabase.from("categories").delete().eq("id", id);

    if (!error) {
      const updatedCategories = categories.filter((cat) => cat.id !== id);
      setCategories(updatedCategories);
      if (activeTab === id) {
        setActiveTab(updatedCategories.length > 0 ? updatedCategories[0].id : null);
      }
    } else {
      alert("Error: Ensure this category is empty before deleting.");
    }
  };

  // --- Size Guide Row Handlers (Logic remains same) ---
  const addSizeRow = () => {
    const newRow = {
      category_id: activeTab,
      size_label: "M",
      measurement_1_name: "Underbust",
      measurement_1_value: "32-34",
      measurement_2_name: "Overbust",
      measurement_2_value: "36-38",
      position: sizeGuides.length,
      is_new: true,
      id: Math.random().toString(36).substr(2, 9)
    };
    setSizeGuides([...sizeGuides, newRow]);
  };

  const handleRowChange = (id, field, value) => {
    setSizeGuides(prev => prev.map(row => row.id === id ? { ...row, [field]: value } : row));
  };

  const deleteRow = async (id, isNew) => {
    if (isNew) {
      setSizeGuides(prev => prev.filter(row => row.id !== id));
      return;
    }
    const { error } = await supabase.from("size_guides").delete().eq("id", id);
    if (!error) setSizeGuides(prev => prev.filter(row => row.id !== id));
  };

  const handleSave = async () => {
    setSaving(true);
    setMessage({ type: "", text: "" });

    try {
      const rowsToUpsert = sizeGuides.map(({ is_new, ...rest }) => {
        if (is_new) {
            const { id, ...newRest } = rest;
            return newRest;
        }
        return rest;
      });

      const { error } = await supabase.from("size_guides").upsert(rowsToUpsert);
      if (error) throw error;
      
      setMessage({ type: "success", text: "Size configurations synced successfully!" });
      fetchData(); 
    } catch (err) {
      setMessage({ type: "error", text: err.message });
    } finally {
      setSaving(false);
    }
  };

  if (loading) return (
    <div className="flex items-center justify-center min-h-screen bg-slate-50">
      <Loader2 className="animate-spin text-pink-600" size={40} />
    </div>
  );

  return (
    <div className="min-h-screen bg-slate-50/50 p-4 md:p-10 font-sans text-slate-900">
      <div className="max-w-6xl mx-auto">
        
        {/* --- HEADER --- */}
        <div className="flex flex-col md:flex-row md:items-center justify-between mb-10 gap-4">
          <div>
            <h1 className="text-2xl font-black italic uppercase tracking-tight">Size & Fit Engine</h1>
            <p className="text-slate-500 text-sm">Configure dynamic dimensions and navigation visibility.</p>
          </div>
          <button 
            onClick={handleSave}
            disabled={saving}
            className="bg-pink-600 text-white px-10 py-3 rounded-2xl text-xs font-black uppercase tracking-widest flex items-center gap-2 hover:bg-slate-900 shadow-xl shadow-pink-100 transition-all disabled:opacity-50"
          >
            {saving ? <Loader2 size={18} className="animate-spin" /> : <Save size={18} />}
            Sync Measurements
          </button>
        </div>

        {/* --- FEEDBACK TOAST --- */}
        {message.text && (
          <div className={`mb-8 p-4 rounded-2xl flex items-center gap-3 border animate-in fade-in slide-in-from-top-2 ${
            message.type === "success" ? "bg-emerald-50 border-emerald-100 text-emerald-700" : "bg-red-50 border-red-100 text-red-700"
          }`}>
            {message.type === "success" ? <CheckCircle2 size={18}/> : <AlertCircle size={18}/>}
            <span className="text-[10px] font-black uppercase tracking-wider">{message.text}</span>
          </div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
          
          {/* --- LEFT: CATEGORY NAV --- */}
          <div className="lg:col-span-4 space-y-4">
            <section className="bg-white p-6 rounded-[32px] border border-slate-200 shadow-sm">
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-[10px] font-black text-slate-400 uppercase tracking-[0.2em] flex items-center gap-2">
                  <Layers size={14}/> Collections
                </h3>
                <span className="text-[9px] font-bold text-slate-400 uppercase tracking-widest flex items-center gap-1">
                   <Eye size={10}/> Use this for Header Status
                </span>
              </div>

              <div className="space-y-2">
                {categories.map((cat) => (
                    <div key={cat.id} className="group relative">
                      <button
                        onClick={() => setActiveTab(cat.id)}
                        className={`w-full flex items-center justify-between p-4 pr-12 rounded-2xl text-xs font-bold transition-all ${
                          activeTab === cat.id 
                          ? "bg-pink-50 text-pink-600 shadow-sm" 
                          : "text-slate-500 hover:bg-slate-50"
                        }`}
                      >
                        <div className="flex items-center gap-2 truncate max-w-[150px]">
                          <span>{cat.name}</span>
                        </div>
                        <ChevronRight size={14} className={activeTab === cat.id ? "opacity-100" : "opacity-0"} />
                      </button>

                      {/* Floating Admin Controls */}
                      <div className="absolute right-3 top-1/2 -translate-y-1/2 flex items-center gap-2 bg-white/80 backdrop-blur p-1 rounded-xl shadow-sm opacity-80 group-hover:opacity-100 transition-opacity">
                         {/* TOGGLE HEADER VISIBILITY */}
                         <button 
                            onClick={(e) => { e.stopPropagation(); toggleHeaderVisibility(cat.id, cat.on_header); }}
                            className={`p-1.5 rounded-lg transition-colors ${cat.on_header ? 'text-pink-600 bg-pink-50' : 'text-slate-300 hover:bg-slate-100'}`}
                            title={cat.on_header ? "Visible in Header" : "Hidden from Header"}
                         >
                            {cat.on_header ? <Eye size={14} /> : <EyeOff size={14} />}
                         </button>
                         
                         <button 
                            onClick={(e) => { e.stopPropagation(); editCategory(cat.id, cat.name); }}
                            className="p-1.5 text-slate-400 hover:text-blue-500 hover:bg-blue-50 rounded-lg transition-colors"
                         >
                            <Edit2 size={14} />
                         </button>
                         <button 
                            onClick={(e) => { e.stopPropagation(); deleteCategory(cat.id, cat.name); }}
                            className="p-1.5 text-slate-400 hover:text-red-500 hover:bg-red-50 rounded-lg transition-colors"
                         >
                            <Trash2 size={14} />
                         </button>
                      </div>
                    </div>
                ))}
                
                <button 
                  onClick={addCategory}
                  className="w-full mt-4 p-4 border-2 border-dashed border-slate-100 rounded-2xl text-[10px] font-black uppercase tracking-widest text-slate-400 hover:border-pink-200 hover:text-pink-600 transition-all"
                >
                  + Add Category
                </button>
              </div>
            </section>

            {/* Quick Stats / Info */}
            <div className="bg-white p-6 rounded-[32px] border border-slate-200">
               <h4 className="text-[10px] font-black uppercase tracking-widest text-slate-400 mb-4 flex items-center gap-2">
                 <LayoutPanelTop size={14}/> Menu Overview
               </h4>
               <div className="flex items-center justify-between text-xs font-bold text-slate-600">
                 <span>Active in Header</span>
                 <span className="text-pink-600">{categories.filter(c => c.on_header).length}</span>
                 
               </div>
               <p className="text-xs text-slate-500">
                 Do not show more than 5 categories in the header
               </p>
            </div>
          </div>

          {/* --- RIGHT: MEASUREMENT EDITOR --- */}
          <div className="lg:col-span-8 space-y-6">
            <section className="bg-white p-8 rounded-[40px] border border-slate-200 shadow-sm min-h-[400px]">
              <div className="flex items-center justify-between mb-8">
                <h3 className="text-xs font-black text-pink-600 uppercase tracking-widest flex items-center gap-2">
                  <Ruler size={16}/> Dimension Logic: <span className="text-slate-900">{categories.find(c => c.id === activeTab)?.name}</span>
                </h3>
                <button 
                  onClick={addSizeRow}
                  className="flex items-center gap-2 text-[10px] font-black uppercase tracking-widest text-slate-900 hover:text-pink-600 transition-colors"
                >
                  <Plus size={14} /> Add Size Row
                </button>
              </div>

              {/* TABLE LOGIC REMAINS THE SAME... */}
              <div className="space-y-4">
                <div className="grid grid-cols-12 gap-4 px-4 py-2 text-[9px] font-black text-slate-400 uppercase tracking-widest">
                  <div className="col-span-2">Label</div>
                  <div className="col-span-4">Primary Measurement</div>
                  <div className="col-span-4">Secondary Measurement</div>
                  <div className="col-span-2 text-right">Action</div>
                </div>

                {sizeGuides.filter(row => row.category_id === activeTab).map((row) => (
                  <div key={row.id} className="grid grid-cols-12 gap-4 items-center p-4 bg-slate-50 rounded-2xl group hover:bg-white hover:ring-2 hover:ring-pink-100 transition-all">
                    <div className="col-span-2">
                      <input 
                        value={row.size_label}
                        onChange={(e) => handleRowChange(row.id, 'size_label', e.target.value)}
                        className="w-full bg-transparent border-none font-black italic text-slate-900 focus:ring-0 text-sm uppercase"
                        placeholder="Size"
                      />
                    </div>
                    <div className="col-span-4 flex gap-2 items-center">
                      <input 
                        value={row.measurement_1_name}
                        onChange={(e) => handleRowChange(row.id, 'measurement_1_name', e.target.value)}
                        className="w-1/2 bg-white/50 border border-slate-100 rounded-lg p-2 text-[9px] font-bold uppercase"
                      />
                      <input 
                        value={row.measurement_1_value}
                        onChange={(e) => handleRowChange(row.id, 'measurement_1_value', e.target.value)}
                        className="w-1/2 bg-white border border-slate-200 rounded-lg p-2 text-xs font-mono"
                      />
                    </div>
                    <div className="col-span-4 flex gap-2 items-center">
                      <input 
                        value={row.measurement_2_name}
                        onChange={(e) => handleRowChange(row.id, 'measurement_2_name', e.target.value)}
                        className="w-1/2 bg-white/50 border border-slate-100 rounded-lg p-2 text-[9px] font-bold uppercase"
                      />
                      <input 
                        value={row.measurement_2_value}
                        onChange={(e) => handleRowChange(row.id, 'measurement_2_value', e.target.value)}
                        className="w-1/2 bg-white border border-slate-200 rounded-lg p-2 text-xs font-mono"
                      />
                    </div>
                    <div className="col-span-2 text-right">
                      <button onClick={() => deleteRow(row.id, row.is_new)} className="p-2 text-slate-300 hover:text-red-500 transition-colors">
                        <Trash2 size={16} />
                      </button>
                    </div>
                  </div>
                ))}

                {sizeGuides.filter(row => row.category_id === activeTab).length === 0 && (
                   <div className="text-center py-12 border-2 border-dashed border-slate-100 rounded-3xl">
                      <p className="text-[10px] font-black uppercase tracking-[0.2em] text-slate-300">No dimensions defined for this selection.</p>
                   </div>
                )}
              </div>
            </section>
          </div>
        </div>
      </div>
    </div>
  );
}